import React from 'react';
import { motion } from 'framer-motion';
export interface BudgetSegment {
  label: string;
  amount: number;
  color?: string;
}
export interface BudgetBarProps {
  totalBudget: number;
  segments: BudgetSegment[];
  showRemaining?: boolean;
  label?: string;
  mode?: 'stimulus' | 'support';
}
function formatEuro(value: number): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}
const SEGMENT_COLORS = [{
  bg: 'bg-blue-500',
  light: 'bg-blue-100',
  text: 'text-blue-700',
  dot: 'bg-blue-500'
}, {
  bg: 'bg-amber-500',
  light: 'bg-amber-100',
  text: 'text-amber-700',
  dot: 'bg-amber-500'
}, {
  bg: 'bg-rose-500',
  light: 'bg-rose-100',
  text: 'text-rose-700',
  dot: 'bg-rose-500'
}, {
  bg: 'bg-purple-500',
  light: 'bg-purple-100',
  text: 'text-purple-700',
  dot: 'bg-purple-500'
}, {
  bg: 'bg-teal-500',
  light: 'bg-teal-100',
  text: 'text-teal-700',
  dot: 'bg-teal-500'
}, {
  bg: 'bg-orange-500',
  light: 'bg-orange-100',
  text: 'text-orange-700',
  dot: 'bg-orange-500'
}];
export function BudgetBar({
  totalBudget,
  segments,
  showRemaining = true,
  label,
  mode = 'support'
}: BudgetBarProps) {
  if (!totalBudget || totalBudget <= 0) {
    return <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-center" data-id="element-13119" data-mp="1j8trjf2h1ha2">
        <p className="text-red-600 text-sm font-medium" data-id="element-13120" data-mp="vizeoxi5jspt">
          Geen budget opgegeven
        </p>
      </div>;
  }
  const validSegments = (segments || []).filter(s => s.label && typeof s.amount === 'number' && s.amount > 0);
  const totalSpent = validSegments.reduce((sum, s) => sum + s.amount, 0);
  const remaining = totalBudget - totalSpent;
  const overBudget = remaining < 0;
  const spentPct = Math.min(totalSpent / totalBudget * 100, 100);
  // ─── Stimulus mode: compact bar ─────────────────────────────────────
  if (mode === 'stimulus') {
    return <div className="bg-slate-50 rounded-xl px-4 py-3.5 border border-slate-200" data-id="element-13121" data-mp="7tocdbl5lq9k">
        {label && <p className="text-xs font-semibold text-slate-700 mb-2.5 text-center" data-id="element-13122" data-mp="xhtqq5rk30lr">
            {label}
          </p>}

        {/* Budget header */}
        <div className="flex justify-between items-baseline mb-2" data-id="element-13123" data-mp="169vn9syi7wqr">
          <span className="text-xs text-slate-500" data-id="element-13124" data-mp="16cq7bjdlvfjn">Budget</span>
          <span className="text-sm font-bold text-slate-800 font-mono tabular-nums" data-id="element-13125" data-mp="1b85ksa7pb9fj">
            {formatEuro(totalBudget)}
          </span>
        </div>

        {/* Bar */}
        <div className="h-5 bg-slate-200 rounded-full overflow-hidden flex" data-id="element-13126" data-mp="ik1b7dm0ec7k">
          {validSegments.map((seg, i) => {
          const pct = seg.amount / totalBudget * 100;
          const colors = SEGMENT_COLORS[i % SEGMENT_COLORS.length];
          return <motion.div key={i} initial={{
            width: 0
          }} animate={{
            width: `${Math.min(pct, 100)}%`
          }} transition={{
            delay: i * 0.1,
            duration: 0.5,
            ease: 'easeOut'
          }} className={`${colors.bg} h-full ${i === 0 ? 'rounded-l-full' : ''}`} title={`${seg.label}: ${formatEuro(seg.amount)}`} data-id="element-13127" data-mp="sgw85s2mw4jq" data-mpi={i} />;
        })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2" data-id="element-13128" data-mp="3eqdi6841taox">
          {validSegments.map((seg, i) => {
          const colors = SEGMENT_COLORS[i % SEGMENT_COLORS.length];
          return <div key={i} className="flex items-center gap-1" data-id="element-13129" data-mp="39p7ljz79p3a4" data-mpi={i}>
                <div className={`w-2 h-2 rounded-full ${colors.dot}`} data-id="element-13130" data-mp="35qgmvnn5ldkw" />
                <span className="text-xs text-slate-600" data-id="element-13131" data-mp="30q2qj2uwzs2p">{seg.label}</span>
              </div>;
        })}
          {showRemaining && remaining > 0 && <div className="flex items-center gap-1" data-id="element-13132" data-mp="2vs16aelr5mpn">
              <div className="w-2 h-2 rounded-full bg-emerald-400" data-id="element-13133" data-mp="2qsy18u5rvgho" />
              <span className="text-xs text-emerald-600" data-id="element-13134" data-mp="2qusnqcwozgnv">Over</span>
            </div>}
        </div>
      </div>;
  }
  // ─── Support mode: full educational panel ───────────────────────────
  return <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border-2 border-slate-200 shadow-sm overflow-hidden" data-id="element-13135" data-mp="oyyrid8at773">
      {/* Header */}
      <div className="px-5 py-3.5 border-b border-slate-200 bg-white/60" data-id="element-13136" data-mp="1213yajag0dnh">
        <div className="flex items-center justify-between" data-id="element-13137" data-mp="n3tn7b1pp9sa">
          <div data-id="element-13138" data-mp="n3birr4wbp9a">
            <h3 className="text-sm font-bold text-slate-900" data-id="element-13139" data-mp="n5xormc51wpe">
              {label || '💰 Budget overzicht'}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5" data-id="element-13140" data-mp="ebj93inp4k5r">
              Totaal budget: {formatEuro(totalBudget)}
            </p>
          </div>
          <div className={`text-right px-3 py-1.5 rounded-lg ${overBudget ? 'bg-red-100' : 'bg-emerald-100'}`} data-id="element-13141" data-mp="j3j6p4gan0z6">
            
            <div className="text-[10px] text-slate-500 uppercase tracking-wide" data-id="element-13142" data-mp="4b8xg2hb7ksp">
              {overBudget ? 'Tekort' : 'Over'}
            </div>
            <div className={`text-sm font-bold font-mono tabular-nums ${overBudget ? 'text-red-700' : 'text-emerald-700'}`} data-id="element-13143" data-mp="3rhjezzkd5k64">
              
              {formatEuro(Math.abs(remaining))}
            </div>
          </div>
        </div>
      </div>

      {/* Bar visualization */}
      <div className="px-5 py-4" data-id="element-13144" data-mp="37w9gg1mlitcv">
        {/* The bar */}
        <div className="h-8 bg-slate-200 rounded-full overflow-hidden flex mb-4 relative" data-id="element-13145" data-mp="37xu9tnx6c695">
          {validSegments.map((seg, i) => {
          const pct = seg.amount / totalBudget * 100;
          const colors = SEGMENT_COLORS[i % SEGMENT_COLORS.length];
          return <motion.div key={i} initial={{
            width: 0
          }} animate={{
            width: `${Math.min(pct, 100)}%`
          }} transition={{
            delay: i * 0.15,
            duration: 0.6,
            ease: 'easeOut'
          }} className={`${colors.bg} h-full flex items-center justify-center relative ${i === 0 ? 'rounded-l-full' : ''}`} data-id="element-13146" data-mp="3vswzfy3sydqo" data-mpi={i}>
                
                {pct > 12 && <span className="text-[10px] font-bold text-white/90 truncate px-1" data-id="element-13147" data-mp="ig3pc7tt5rze">
                    {Math.round(pct)}%
                  </span>}
              </motion.div>;
        })}
          {/* Remaining space */}
          {remaining > 0 && <div className="flex-1 h-full flex items-center justify-center" data-id="element-13148" data-mp="375uh9gfwsi8j">
              {remaining / totalBudget * 100 > 12 && <span className="text-[10px] font-semibold text-slate-400" data-id="element-13149" data-mp="3b76di4tag1pp">
                  {Math.round(remaining / totalBudget * 100)}%
                </span>}
            </div>}
        </div>

        {/* Segment breakdown */}
        <div className="space-y-2" data-id="element-13150" data-mp="3pyyflf42nj9h">
          {validSegments.map((seg, i) => {
          const pct = seg.amount / totalBudget * 100;
          const colors = SEGMENT_COLORS[i % SEGMENT_COLORS.length];
          return <motion.div key={i} initial={{
            opacity: 0,
            x: -8
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: i * 0.1 + 0.3,
            duration: 0.3
          }} className={`flex items-center justify-between ${colors.light} rounded-lg px-3 py-2`} data-id="element-13151" data-mp="2th8asz6qriy" data-mpi={i}>
                
                <div className="flex items-center gap-2" data-id="element-13152" data-mp="29m03zab96clw">
                  <div className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} data-id="element-13153" data-mp="221fsdloy9e7u" />
                  <span className={`text-sm font-medium ${colors.text}`} data-id="element-13154" data-mp="1x9nyb16l4qcy">
                    {seg.label}
                  </span>
                </div>
                <div className="flex items-baseline gap-2" data-id="element-13155" data-mp="1s8rxiwhj5kbr">
                  <span className="text-xs text-slate-400" data-id="element-13156" data-mp="1nct0kki4ytzq">
                    {Math.round(pct)}%
                  </span>
                  <span className={`text-sm font-bold font-mono tabular-nums ${colors.text}`} data-id="element-13157" data-mp="2kyqqh2yui1lp">
                    
                    {formatEuro(seg.amount)}
                  </span>
                </div>
              </motion.div>;
        })}

          {/* Remaining */}
          {showRemaining && <motion.div initial={{
          opacity: 0,
          x: -8
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: validSegments.length * 0.1 + 0.3,
          duration: 0.3
        }} className={`flex items-center justify-between rounded-lg px-3 py-2 ${overBudget ? 'bg-red-100' : 'bg-emerald-100'}`} data-id="element-13158" data-mp="26z7iqb9y4s9x">
            
              <div className="flex items-center gap-2" data-id="element-13159" data-mp="2zoorv88dma0r">
                <div className={`w-2.5 h-2.5 rounded-full ${overBudget ? 'bg-red-500' : 'bg-emerald-500'}`} data-id="element-13160" data-mp="2zm2cimf8uq7z" />
              
                <span className={`text-sm font-medium ${overBudget ? 'text-red-700' : 'text-emerald-700'}`} data-id="element-13161" data-mp="2qn8hv1lh49x2">
                
                  {overBudget ? 'Tekort' : 'Over'}
                </span>
              </div>
              <span className={`text-sm font-bold font-mono tabular-nums ${overBudget ? 'text-red-700' : 'text-emerald-700'}`} data-id="element-13162" data-mp="3idv3kbar61d2">
              
                {formatEuro(Math.abs(remaining))}
              </span>
            </motion.div>}
        </div>
      </div>

      {/* Educational tip */}
      <div className="bg-white/70 px-5 py-3 border-t border-slate-100" data-id="element-13163" data-mp="16j6vljh6a2x">
        <div className="flex items-start gap-2.5" data-id="element-13164" data-mp="5xj7p1vptcap">
          <span className="text-slate-400 text-sm mt-0.5" data-id="element-13165" data-mp="5z2l8kpzl98s">💡</span>
          <p className="text-xs text-slate-700 leading-relaxed" data-id="element-13166" data-mp="fbtdrh5p1m3u">
            <strong data-id="element-13167" data-mp="f9ypbsv7rbv0">Tip:</strong>{' '}
            {overBudget ? 'De uitgaven zijn hoger dan het budget. Kijk welke uitgave je kunt verlagen of weglaten.' : `Je hebt ${Math.round(remaining / totalBudget * 100)}% van je budget over. Dat is ${formatEuro(remaining)}.`}
          </p>
        </div>
      </div>
    </div>;
}